import { EXPENSE_CATEGORIES } from './expense-categories.js';

function parseAmount(token) {
  const n = Number(String(token).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function guessCategory(text) {
  const t = text.toLowerCase();
  if (/bigliett|ingresso|funivia|seggiovia|castello|pletna|barca|museo|mura|grotte|vignetta|terme/.test(t)) {
    return 'biglietti';
  }
  if (/parchegg|parking|benzina|carburante|autostrada|vignetta|trasporto/.test(t)) {
    return 'trasporto';
  }
  if (/pranzo|cena|bar|rifugio|pasto|cibo|contanti/.test(t)) {
    return 'cibo';
  }
  if (/alloggio|hotel|appart/.test(t)) {
    return 'alloggio';
  }
  return 'altro';
}

/**
 * Parse free-text cost_info from plan stops into expense suggestions.
 * @param {string} costInfo
 * @returns {{
 *   suggestedAmount: number | null,
 *   amountMin: number | null,
 *   amountMax: number | null,
 *   subLabel: string | null,
 *   category: string,
 *   preview: string,
 *   hasNumericHint: boolean,
 * }}
 */
export function parsePlannedCost(costInfo) {
  const preview = String(costInfo ?? '').trim();
  const cleaned = preview.replace(/\s*—\s*indicativo.*$/i, '').trim();

  let focusText = cleaned;
  let subLabel = null;

  const parenMatch = cleaned.match(/\(([^)]+)\)/);
  if (parenMatch) {
    const inner = parenMatch[1];
    if (/parcheggio|parking/i.test(inner)) {
      focusText = inner;
      subLabel = 'Parcheggio';
    } else if (/pranzo|cena|bar|rifugio|pasto/i.test(inner)) {
      focusText = inner;
      subLabel = 'Pasto';
    }
  }

  const category = guessCategory(subLabel ? `${subLabel} ${cleaned}` : cleaned);

  const rangeMatch = focusText.match(/€\s*(\d+(?:[.,]\d+)?)\s*[–-]\s*(\d+(?:[.,]\d+)?)/);
  if (rangeMatch) {
    const amountMin = parseAmount(rangeMatch[1]);
    const amountMax = parseAmount(rangeMatch[2]);
    const suggestedAmount =
      amountMin != null && amountMax != null
        ? Math.round(((amountMin + amountMax) / 2) * 100) / 100
        : amountMin ?? amountMax;
    return {
      suggestedAmount,
      amountMin,
      amountMax,
      subLabel,
      category,
      preview,
      hasNumericHint: true,
    };
  }

  const amounts = [...focusText.matchAll(/€\s*~?\s*(\d+(?:[.,]\d+)?)/g)]
    .map((m) => parseAmount(m[1]))
    .filter((n) => n != null);

  if (amounts.length) {
    return {
      suggestedAmount: amounts[0],
      amountMin: amounts.length > 1 ? Math.min(...amounts) : amounts[0],
      amountMax: amounts.length > 1 ? Math.max(...amounts) : amounts[0],
      subLabel,
      category,
      preview,
      hasNumericHint: true,
    };
  }

  return {
    suggestedAmount: null,
    amountMin: null,
    amountMax: null,
    subLabel,
    category,
    preview,
    hasNumericHint: /gratis/i.test(cleaned) ? false : false,
  };
}

/**
 * @param {{ name: string, cost_info?: string, costInfo?: string }} stop
 * @param {string} dayKey
 */
export function suggestExpenseFromStop(stop, dayKey) {
  const costInfo = stop.cost_info ?? stop.costInfo ?? '';
  const parsed = parsePlannedCost(costInfo);
  const label = parsed.subLabel ? `${parsed.subLabel} — ${stop.name}` : stop.name;

  let amountHint = '';
  if (parsed.amountMin != null && parsed.amountMax != null && parsed.amountMin !== parsed.amountMax) {
    amountHint = `€${parsed.amountMin}–${parsed.amountMax}`;
  }

  return {
    dayKey,
    label,
    amount: parsed.suggestedAmount,
    category: parsed.category,
    costPreview: parsed.preview,
    amountHint,
    hasNumericHint: parsed.hasNumericHint,
  };
}

export function categoryOptionsHtml(selected = 'altro') {
  return EXPENSE_CATEGORIES.map(
    (c) => `<option value="${c.id}"${c.id === selected ? ' selected' : ''}>${c.label}</option>`,
  ).join('');
}
